import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import Icon from '@spark/ui-components/components/Icon';
import TruncatedText from '@spark/ui-components/components/TanStackTable/components/TruncatedText';

const actionButtonStyle = {
  border: 'none',
  background: 'transparent',
  padding: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer'
};

const stopPropagation = (event) => {
  event.stopPropagation();
};

const InlineTextCell = ({
  value,
  displayMaxLength,
  maxLength,
  isEditing,
  isSaving,
  editingValue,
  onStartEdit,
  onCommit,
  onCancel,
  usePopover = false,
  suggestions = [],
  isSearching = false,
  onSearch = null,
  onSelectSuggestion = null,
  renderSuggestion = null,
  selectionRequired = false,
  emptySuggestionsText = 'Nenhum resultado encontrado.',
  initialPopoverPosition = null,
  onClear = null,
  clearLabel = 'Remover valor',
  extraAction = null,
  adornment = null,
  actionsAtEnd = false
}) => {
  const inputRef = useRef(null);
  const didFinishRef = useRef(false);
  const wasEditingRef = useRef(false);
  const skipSearchValueRef = useRef(null);
  const lastSearchTermRef = useRef(null);
  const searchRequestIdRef = useRef(0);
  const [localValue, setLocalValue] = useState('');
  const [localSuggestions, setLocalSuggestions] = useState([]);
  const [localIsSearching, setLocalIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState(null);
  const onSearchRef = useRef(onSearch);

  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  const buscarSugestoes = (term) => {
    const texto = String(term || '');
    if (!onSearchRef.current || lastSearchTermRef.current === texto) return;

    lastSearchTermRef.current = texto;
    const requestId = ++searchRequestIdRef.current;
    setHasSearched(true);
    setLocalIsSearching(true);
    setLocalSuggestions([]);
    Promise.resolve(onSearchRef.current(texto))
      .then((registros) => {
        if (requestId === searchRequestIdRef.current) setLocalSuggestions(Array.isArray(registros) ? registros.slice(0, 5) : []);
      })
      .catch(() => {
        if (requestId === searchRequestIdRef.current) setLocalSuggestions([]);
      })
      .finally(() => {
        if (requestId === searchRequestIdRef.current) setLocalIsSearching(false);
      });
  };

  useEffect(() => {
    if (!isEditing || isSaving) {
      didFinishRef.current = false;
      wasEditingRef.current = false;
      lastSearchTermRef.current = null;
      return;
    }

    if (!wasEditingRef.current) {
      const initialValue = editingValue ?? value ?? '';
      skipSearchValueRef.current = initialValue;
      setLocalValue(initialValue);
    }

    wasEditingRef.current = true;
    didFinishRef.current = false;

    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select?.();
    }
  }, [isEditing, isSaving, editingValue, value]);

  useEffect(() => {
    if (!isEditing || isSaving || !onSearchRef.current) return undefined;
    if (skipSearchValueRef.current === localValue) {
      skipSearchValueRef.current = null;
      return undefined;
    }
    const timeout = window.setTimeout(() => buscarSugestoes(localValue), 250);
    return () => window.clearTimeout(timeout);
  }, [isEditing, isSaving, localValue]);

  const handleCommit = () => {
    if (didFinishRef.current || isSaving) return;
    if (selectionRequired) return;
    didFinishRef.current = true;
    onCommit(localValue);
  };

  const handleCancel = () => {
    if (didFinishRef.current || isSaving) return;
    didFinishRef.current = true;
    onCancel();
  };

  // Com selectionRequired não dá para salvar o campo vazio; o botão de remover é o caminho explícito para limpar.
  const handleClear = () => {
    if (didFinishRef.current || isSaving || !onClear) return;
    didFinishRef.current = true;
    onClear();
  };

  const handleStartEdit = (event) => {
    let nextPopoverPosition = null;
    if (usePopover) {
      const rect = event.currentTarget.getBoundingClientRect();
      nextPopoverPosition = { top: rect.bottom + 6, left: rect.left };
      setPopoverPosition(nextPopoverPosition);
    }
    buscarSugestoes('');
    onStartEdit({ popoverPosition: nextPopoverPosition });
  };

  const handleBlur = (event) => {
    const form = event.currentTarget.form;
    if (form && event.relatedTarget && form.contains(event.relatedTarget)) {
      return;
    }

    if (selectionRequired) {
      handleCancel();
      return;
    }
    handleCommit();
  };

  if (isSaving) {
    return (
      <div
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 28 }}
        onClick={stopPropagation}
      >
        <span className="spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true" />
        <span>Salvando...</span>
      </div>
    );
  }

  const editor = (
    <form
        onSubmit={(event) => {
          event.preventDefault();
          stopPropagation(event);
          handleCommit();
        }}
        style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: usePopover ? 320 : 160, width: '100%' }}
        onClick={stopPropagation}
        onMouseDown={stopPropagation}
        onPointerDown={stopPropagation}
      >
        <input
          ref={inputRef}
          type="text"
          value={localValue}
          maxLength={maxLength}
          onChange={(event) => setLocalValue(event.target.value)}
          onBlur={handleBlur}
          onKeyDown={(event) => {
            event.stopPropagation();

            if (event.key === 'Escape') {
              event.preventDefault();
              handleCancel();
            }
          }}
          onClick={stopPropagation}
          onMouseDown={stopPropagation}
          onPointerDown={stopPropagation}
          style={{
            width: '100%',
            minWidth: 120,
            height: 30,
            border: '1px solid #ced4da',
            borderRadius: 4,
            padding: '0 8px'
          }}
        />
        {onClear && String(value ?? '').trim() ? (
          <button
            type="button"
            aria-label={clearLabel}
            title={clearLabel}
            onMouseDown={(event) => event.preventDefault()}
            onPointerDown={(event) => event.preventDefault()}
            onClick={(event) => {
              stopPropagation(event);
              handleClear();
            }}
            style={{ ...actionButtonStyle, color: '#c62828' }}
          >
            <Icon name="trash" size={16} />
          </button>
        ) : null}
        <button
          type="button"
          aria-label="Cancelar edicao"
          onMouseDown={(event) => event.preventDefault()}
          onPointerDown={(event) => event.preventDefault()}
          onClick={(event) => {
            stopPropagation(event);
            handleCancel();
          }}
          style={actionButtonStyle}
        >
          <Icon name="close-circle-outline" size={16} />
        </button>
        <button
          type="submit"
          aria-label="Salvar edicao"
          onMouseDown={(event) => event.preventDefault()}
          onPointerDown={(event) => event.preventDefault()}
          onClick={stopPropagation}
          style={{ ...actionButtonStyle, color: '#198754' }}
        >
          <Icon name="check-circle-outline" size={16} />
        </button>
        {(onSearch ? localIsSearching : isSearching) || (onSearch ? localSuggestions : suggestions).length || (selectionRequired && hasSearched) ? (
          <div style={{ position: 'absolute', zIndex: 1, top: 34, left: 0, right: 0, maxHeight: 220, overflowY: 'auto', border: '1px solid #ced4da', borderRadius: 4, background: '#ffffff', boxShadow: '0 6px 14px rgba(15, 23, 42, 0.16)' }}>
            {(onSearch ? localIsSearching : isSearching) ? <div style={{ padding: '8px 10px', color: '#526275', fontSize: 12 }}>Buscando...</div> : (onSearch ? localSuggestions : suggestions).map((suggestion, index) => (
              <button
                key={suggestion.codigo_motorista_tms || suggestion.codigo_veiculo_tms || index}
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={(event) => {
                  stopPropagation(event);
                  onSelectSuggestion?.(suggestion);
                }}
                style={{ display: 'block', width: '100%', padding: '8px 10px', border: 0, background: '#ffffff', color: '#24344d', textAlign: 'left', fontSize: 12 }}
              >
                {renderSuggestion?.(suggestion) || String(suggestion)}
              </button>
            ))}
            {!(onSearch ? localIsSearching : isSearching) && !(onSearch ? localSuggestions : suggestions).length && selectionRequired ? <div style={{ padding: '8px 10px', color: '#526275', fontSize: 12 }}>{emptySuggestionsText}</div> : null}
            {(() => {
              // Saída quando o valor não está entre as sugestões (ex.: usar um motorista ou placa fora do TMS).
              if ((onSearch ? localIsSearching : isSearching) || !extraAction) return null;
              const acao = extraAction(localValue);
              if (!acao) return null;
              return (
                <button
                  type="button"
                  disabled={acao.disabled}
                  title={acao.title}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={(event) => {
                    stopPropagation(event);
                    if (acao.disabled || didFinishRef.current) return;
                    didFinishRef.current = true;
                    acao.onSelect(localValue);
                  }}
                  style={{ display: 'block', width: '100%', padding: '8px 10px', border: 0, borderTop: '1px solid #e2e8f0', background: '#f8fafc', color: acao.disabled ? '#8a96a3' : '#163455', textAlign: 'left', fontSize: 12, fontWeight: 600, cursor: acao.disabled ? 'default' : 'pointer' }}
                >
                  {acao.label}
                </button>
              );
            })()}
          </div>
        ) : null}
    </form>
  );

  if (isEditing) {
    const resolvedPopoverPosition = popoverPosition || initialPopoverPosition;
    if (usePopover && resolvedPopoverPosition && typeof document !== 'undefined') {
      return createPortal(
        <div
          style={{
            position: 'fixed',
            zIndex: 1300,
            top: resolvedPopoverPosition.top,
            left: resolvedPopoverPosition.left,
            minWidth: 320,
            maxWidth: 'calc(100vw - 32px)',
            padding: 8,
            border: '1px solid #ced4da',
            borderRadius: 6,
            background: '#ffffff',
            boxShadow: '0 8px 20px rgba(15, 23, 42, 0.2)'
          }}
        >
          {editor}
        </div>,
        document.body
      );
    }

    return editor;
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={(event) => {
        stopPropagation(event);
        handleStartEdit(event);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          stopPropagation(event);
          handleStartEdit(event);
        }
      }}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        width: '100%',
        minHeight: 24,
        cursor: 'pointer'
      }}
      title="Clique para editar"
    >
      <TruncatedText value={value || '-'} maxLength={displayMaxLength} />
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0, marginLeft: actionsAtEnd ? 'auto' : 0 }}>
        {adornment ? (
          // Selos com ação própria (ex.: cadastrar no TMS) não abrem a edição da célula.
          <span
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
            onClick={stopPropagation}
            onKeyDown={stopPropagation}
          >
            {adornment}
          </span>
        ) : null}
        <Icon name="pencil" size={12} />
      </span>
    </div>
  );
};

InlineTextCell.propTypes = {
  value: PropTypes.string,
  displayMaxLength: PropTypes.number,
  maxLength: PropTypes.number,
  isEditing: PropTypes.bool,
  isSaving: PropTypes.bool,
  editingValue: PropTypes.string,
  onStartEdit: PropTypes.func.isRequired,
  onCommit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  usePopover: PropTypes.bool,
  suggestions: PropTypes.arrayOf(PropTypes.object),
  isSearching: PropTypes.bool,
  onSearch: PropTypes.func,
  onSelectSuggestion: PropTypes.func,
  renderSuggestion: PropTypes.func,
  selectionRequired: PropTypes.bool,
  onClear: PropTypes.func,
  clearLabel: PropTypes.string,
  emptySuggestionsText: PropTypes.string,
  // (texto digitado) => { label, onSelect, disabled?, title? } | null: ação no fim da lista de sugestões.
  extraAction: PropTypes.func,
  // Selos entre o texto e o lápis (fora do TMS, status...).
  adornment: PropTypes.node,
  // Lápis e selos encostados no fim da célula: ficam alinhados um abaixo do outro na coluna.
  actionsAtEnd: PropTypes.bool,
  initialPopoverPosition: PropTypes.shape({
    top: PropTypes.number,
    left: PropTypes.number
  })
};

InlineTextCell.defaultProps = {
  value: '',
  displayMaxLength: 30,
  maxLength: 500,
  isEditing: false,
  isSaving: false,
  editingValue: '',
  usePopover: false,
  suggestions: [],
  isSearching: false,
  onSearch: null,
  onSelectSuggestion: null,
  renderSuggestion: null,
  selectionRequired: false,
  onClear: null,
  clearLabel: 'Remover valor',
  emptySuggestionsText: 'Nenhum resultado encontrado.',
  initialPopoverPosition: null,
  extraAction: null,
  adornment: null,
  actionsAtEnd: false
};

export default InlineTextCell;
